import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Checkbox,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Popover,
    TextField,
    Typography
} from '@mui/material'
import { Close, DeleteSweep, DoneAll, KeyboardArrowDown, Search } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/store'
import { useFetchDataQuery } from '../../services/services'
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks'

type CancelCard = {
    cardNo: string;
    embossName?: string;
    cardName?: string;
    customerName?: string;
    status?: string;
    id?: number | string;
}

const statusFilters = [
    {
        field: "status",
        logicOperator: "and",
        // İptal (I), vadesi dolmuş (V) ve stoplist (S) kartlar tekrar iptale geçirilemez.
        values: ["I","V","S"],
        valueType: 1,
        operationType: 12
    }
]

interface UserInfo {
    companyId: number;
    customerId: number;
}

interface CancelCardFormProps {
    onClose: () => void
    isOpen?: boolean
}

export interface CancelCardFormRef {
    getFormData: () => {
        userData: UserInfo | null
        selectedCards: CancelCard[]
        cancelReasonDescription: string
    }
    validateForm: () => boolean
}

const CancelCardForm = forwardRef<CancelCardFormRef, CancelCardFormProps>(({ onClose: _onClose, isOpen = false }, ref) => {
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState)

    const [selectedCards, setSelectedCards] = useState<CancelCard[]>([])
    const [allCards, setAllCards] = useState<CancelCard[]>([])
    const [cardSearchText, setCardSearchText] = useState('')
    const [cancelReasonDescription, setCancelReasonDescription] = useState('')
    const [error, setError] = useState('')

    // İptal modalı kartları: /Card/CustomerMealCardsV2
    const listUrl = `/Card/CustomerMealCardsV2?customerId=${currentUserState?.companyId}`
    const [requestBody, setRequestBody] = useState({
        filters: statusFilters as any[],
        pageSize: 25,
        page: 1,
        orderBy: "",
        orderDesc: false,
        customerId: currentUserState?.companyId || undefined
    })

    const [currentPage, setCurrentPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const isLoadingMoreRef = useRef<boolean>(false)
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const { data: fetchData, isLoading: isFetchLoading, isFetching, error: queryError, refetch } = useFetchDataQuery(
        { url: listUrl, filter: requestBody, method: "POST" },
        {
            skip: !requestBody?.customerId || !isOpen,
            refetchOnMountOrArgChange: false
        }
    )

    const isRequestLoading = isFetching || isFetchLoading

    // customerId değişince request body güncelle
    useEffect(() => {
        if (currentUserState?.companyId) {
            setRequestBody(prev => ({ ...prev, customerId: currentUserState.companyId }))
        }
    }, [currentUserState?.companyId])

    // Modal açıldığında state resetle (kartlar/search)
    useEffect(() => {
        if (isOpen && currentUserState?.companyId) {
            setSelectedCards([])
            setCardSearchText('')
            setCancelReasonDescription('')
            setError('')

            setCurrentPage(1)
            setHasMore(true)
            setIsLoadingMore(false)
            isLoadingMoreRef.current = false

            setRequestBody(prev => ({
                ...prev,
                page: 1,
                filters: statusFilters as any[],
                customerId: currentUserState.companyId
            }))
        }
    }, [isOpen, currentUserState?.companyId])

    // Modalı yeniden açınca cache argümanı aynı kalırsa fetch atlanabiliyor.
    // Bu yüzden isOpen olduğunda mutlaka refetch tetikliyoruz.
    useEffect(() => {
        if (!isOpen || !currentUserState?.companyId) return
        refetch()
    }, [isOpen, currentUserState?.companyId, refetch])

    // API hata durumunu ekranda göster
    useEffect(() => {
        if (queryError) {
            setError('Kartlar yüklenirken bir hata oluştu. Lütfen tekrar deneyin.')
        }
    }, [queryError])

    // fetchData / page değişimini takip et
    useEffect(() => {
        if (!fetchData || !isOpen) return
        // Bazı endpoint'ler success alanını farklı gönderebildiği için bunu "opsiyonel" ele alıyoruz.
        const successValue = (fetchData as any)?.success
        if (successValue === false) {
            setHasMore(false)
            setIsLoadingMore(false)
            isLoadingMoreRef.current = false
            if (currentPage === 1) setAllCards([])
            return
        }

        // fetchData ya array ya da { data: [...] } şeklinde gelebilir.
        if (!Array.isArray(fetchData) && (fetchData as any)?.data === null) {
            setHasMore(false)
            setIsLoadingMore(false)
            isLoadingMoreRef.current = false
            if (currentPage === 1) setAllCards([])
            return
        }

        let newCards: any[] = []
        if (Array.isArray(fetchData)) {
            newCards = fetchData
        } else if (Array.isArray((fetchData as any)?.data)) {
            newCards = (fetchData as any).data
        } else if (Array.isArray((fetchData as any)?.data?.data)) {
            newCards = (fetchData as any).data.data
        }

        if (Array.isArray(newCards) && newCards.length > 0) {
            if (currentPage === 1) {
                setAllCards(newCards)
            } else {
                setAllCards(prev => {
                    const existingIds = new Set(prev.map((card: any) => card.id || card.cardNo))
                    const uniqueNewCards = newCards.filter((card: any) => !existingIds.has(card.id || card.cardNo))
                    return [...prev, ...uniqueNewCards]
                })
            }

            if (newCards.length < 25) setHasMore(false)
            else setHasMore(true)
        } else {
            if (currentPage === 1) setAllCards([])
            setHasMore(false)
        }

        setIsLoadingMore(false)
        isLoadingMoreRef.current = false
    }, [fetchData, currentPage, isOpen])

    const filteredCards = useMemo(() => {
        // status filtresi zaten backend tarafında request body ile uygulanıyor.
        // Client tarafında ekstra filtre uygulamayalım ki backend'in döndürdüğü kartlar modalda görünsün.
        return allCards.filter((card) => !!card?.cardNo)
    }, [allCards])

    const toggleCard = (card: CancelCard) => {
        const isSelected = selectedCards.some(c => c.cardNo === card.cardNo)
        if (isSelected) setSelectedCards(prev => prev.filter(c => c.cardNo !== card.cardNo))
        else setSelectedCards(prev => [...prev, card])
    }

    const selectAllCards = () => {
        const newSelected = [...selectedCards]
        filteredCards.forEach(card => {
            if (!newSelected.some(c => c.cardNo === card.cardNo)) newSelected.push(card)
        })
        setSelectedCards(newSelected)
    }

    const clearAllCards = () => setSelectedCards([])

    const popupState = usePopupState({
        variant: 'popover',
        popupId: 'cancel-card-select-popup'
    })

    const isCardSelected = (cardNo: string) => selectedCards.some(c => c.cardNo === cardNo)

    const loadNextPage = () => {
        if (!hasMore || isLoadingMore || isRequestLoading || isLoadingMoreRef.current) return

        isLoadingMoreRef.current = true
        setIsLoadingMore(true)

        const nextPage = currentPage + 1
        setCurrentPage(nextPage)
        setRequestBody(prev => ({ ...prev, page: nextPage }))
    }

    // Search değişince backend'e filters gönder
    useEffect(() => {
        if (!isOpen) return

        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)

        searchTimerRef.current = setTimeout(() => {
            const extraFilters = cardSearchText.trim()
                ? [{
                    field: "cardNo",
                    logicOperator: "and",
                    values: [cardSearchText.trim()],
                    valueType: 1,
                    operationType: 1
                }]
                : []

            setCurrentPage(1)
            setHasMore(true)
            setIsLoadingMore(false)
            isLoadingMoreRef.current = false

            // Boş aramada listeyi temizlemeyelim; aksi halde RTK cache dönüyorsa
            // fetch effect'i tetiklenmeyip modal boş kalabiliyor.
            if (extraFilters.length > 0) {
                setAllCards([])
            }

            setRequestBody(prev => ({
                ...prev,
                page: 1,
                // Hem statü filtresi hem de (varsa) kartNo arama filtresi
                filters: [...(statusFilters as any[]), ...extraFilters]
            }))
        }, 500)

        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        }
    }, [cardSearchText, isOpen])

    // Form verilerini dışarıya açmak için ref
    useImperativeHandle(ref, () => ({
        getFormData: () => {
            const userData: UserInfo = {
                companyId: currentUserState?.companyId || 0,
                customerId: currentUserState?.customerId || 0
            }
            return {
                userData,
                selectedCards,
                cancelReasonDescription: cancelReasonDescription.trim()
            }
        },
        validateForm: () => {
            if (!currentUserState?.customerId) {
                setError('Kullanıcı bilgileri bulunamadı')
                return false
            }

            if (selectedCards.length === 0) {
                setError('Lütfen en az bir kart seçin')
                return false
            }

            if (!cancelReasonDescription.trim()) {
                setError('Lütfen iptal açıklaması girin')
                return false
            }

            setError('')
            return true
        }
    }))

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Kart Seçimi */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Kart Numarası
                </Typography>

                <Box
                    {...bindTrigger(popupState)}
                    sx={{
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        padding: '10px 12px',
                        minHeight: '56px',
                        maxHeight: '120px',
                        overflowY: 'auto',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        cursor: 'pointer',
                        '&:hover': { borderColor: 'primary.main' },
                        position: 'relative',
                        pr: 5
                    }}
                >
                    {selectedCards.length === 0 ? (
                        <Typography sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                            Kart arayın veya seçin
                        </Typography>
                    ) : (
                        selectedCards.map((card) => (
                            <Chip
                                key={card.cardNo}
                                variant="outlined"
                                size="small"
                                color="primary"
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {card.cardNo}
                                        </Typography>
                                        {(card.embossName || card.cardName || card.customerName) && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                                - {card.embossName || card.cardName || card.customerName}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                onDelete={(e) => {
                                    e.stopPropagation()
                                    toggleCard(card)
                                }}
                                sx={{
                                    '& .MuiChip-deleteIcon': {
                                        fontSize: '18px',
                                    }
                                }}
                            />
                        ))
                    )}
                    <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 0.5 }}>
                        {isRequestLoading && currentPage === 1 && <CircularProgress size={20} color="inherit" />}
                        <KeyboardArrowDown sx={{ color: 'text.secondary' }} />
                    </Box>
                </Box>
                <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: { width: 500, mt: 1, maxHeight: 450, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                    }}
                >
                    <>
                        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Kart No ile ara..."
                                value={cardSearchText}
                                onChange={(e) => setCardSearchText(e.target.value)}
                                autoFocus
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: cardSearchText && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setCardSearchText('')}>
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ mb: 1.5 }}
                            />

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DoneAll />}
                                    onClick={selectAllCards}
                                    fullWidth
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                    Tümünü Seç
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteSweep />}
                                    onClick={clearAllCards}
                                    fullWidth
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                    Tümünü Kaldır
                                </Button>
                            </Box>
                        </Box>

                        <List
                            sx={{
                                flexGrow: 1,
                                overflowY: 'auto',
                                maxHeight: 300,
                                '&::-webkit-scrollbar': { width: '8px' },
                                '&::-webkit-scrollbar-track': { background: '#f1f5f9' },
                                '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '4px' }
                            }}
                            onScroll={(e) => {
                                const target = e.currentTarget
                                if (isRequestLoading || isLoadingMore || !hasMore || isLoadingMoreRef.current) return
                                if (target.scrollHeight - target.scrollTop <= target.clientHeight + 30) loadNextPage()
                            }}
                        >
                            {isRequestLoading && currentPage === 1 ? (
                                <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                    <CircularProgress size={40} thickness={4} sx={{ color: '#f59e0b' }} />
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                        Kartlar Yükleniyor...
                                    </Typography>
                                </Box>
                            ) : filteredCards.length === 0 ? (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Kart bulunamadı
                                    </Typography>
                                </Box>
                            ) : (
                                filteredCards.map((card) => (
                                    <ListItem
                                        key={card.cardNo}
                                        button
                                        onClick={() => toggleCard(card)}
                                        sx={{ py: 0.5 }}
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={isCardSelected(card.cardNo)}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <ListItemText
                                            primary={card.cardNo}
                                            secondary={card.cardName || card.embossName || card.customerName}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                            secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem' } }}
                                        />
                                    </ListItem>
                                ))
                            )}

                            {(isLoadingMore || isLoadingMoreRef.current || (isRequestLoading && currentPage > 1)) && hasMore && (
                                <ListItem sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 2,
                                    background: 'linear-gradient(to bottom, transparent, rgba(245, 158, 11, 0.05))',
                                    borderTop: '1px solid #f1f5f9',
                                    width: '100%'
                                }}>
                                    <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                                        <CircularProgress
                                            size={24}
                                            thickness={4}
                                            sx={{ color: '#e2e8f0' }}
                                            variant="determinate"
                                            value={100}
                                        />
                                        <CircularProgress
                                            size={24}
                                            thickness={4}
                                            sx={{ color: '#f59e0b', position: 'absolute', left: 0 }}
                                            variant="indeterminate"
                                        />
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#f59e0b',
                                            fontWeight: 700,
                                            letterSpacing: '0.5px',
                                            textTransform: 'uppercase',
                                            fontSize: '0.65rem'
                                        }}
                                    >
                                        Kartlar Yükleniyor
                                    </Typography>
                                </ListItem>
                            )}
                        </List>
                    </>
                </Popover>
            </Box>

            {/* Açıklama Alanı */}
            <Box sx={{ mb: 0 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Açıklama
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="Açıklama girin"
                    value={cancelReasonDescription}
                    onChange={(e) => setCancelReasonDescription(e.target.value)}
                    error={!cancelReasonDescription.trim() && selectedCards.length > 0}
                    helperText={!cancelReasonDescription.trim() && selectedCards.length > 0 ? 'Lütfen geçerli bir açıklama giriniz.' : ''}
                />
            </Box>
        </Box>
    )
})

export default CancelCardForm
